// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { z } from 'zod';
import type { Address, PlatformPaymentMethods } from './backend_config.types';
import { AddressSchema, PlatformPaymentMethodsSchema } from './backend_config.types';
import type { JsonValue } from './common.types';
import type { FinalizeSendPaymentRequestResponse, TransactionDetails } from './payment.types';
import { CardType, TerminalCardEntryMode } from './payment.types';
import {
    FinalizeSendPaymentRequestResponseSchema,
    TransactionDetailsSchema,
} from './schemas/payment.schemas';
import {
    JsonNonnegativeLosslessNumberInputSchema,
    JsonValueSchema,
} from './schemas/common.schemas';
import { NumericIpgSystemIdSchema } from './schemas/third-party-system.schemas';
import type { LosslessNumber } from './lossless-json-codec';
import type { SupportedIpgSystemId } from './types/third-party-system.enums';
import {
    CardPaymentInitialTransactionDetailsSchema,
    EInvoiceInitialScreenDataSchema,
    ReconcilePaymentsDataSchema,
    ReconciliationTargetKind,
    ReconciliationTargetSchema,
    StateType,
    createGenericTransactionDetailsDraft,
    type CardPaymentInitialTransactionDetails,
    type EInvoiceAppStateFields,
    type EInvoiceInitialScreenData,
    type ReconcilePaymentsData,
    type ReconciliationTarget,
    type SidebarConfig,
} from './sidebar-app-state-foundation';

export {
    DealertrackScreenId,
    DealertrackScreenIdSchema,
    DealertrackScreenIdValues,
} from './types/ipw/dealertrack/dealertrack-screen-id';
export {
    CardPaymentInitialTransactionDetailsSchema,
    EInvoiceInitialScreenDataSchema,
    ReconcilePaymentsDataSchema,
    ReconciliationTargetKind,
    ReconciliationTargetSchema,
    StateType,
    createGenericTransactionDetailsDraft,
};
export type {
    CardPaymentInitialTransactionDetails,
    EInvoiceInitialScreenData,
    ReconcilePaymentsData,
    ReconciliationTarget,
    SidebarConfig,
};

export interface ListDisplayAction {
    workflowId: string;
    label: string;
}

export interface ListDisplayData {
    items: Array<Record<string, unknown>>;
}

export interface ObjectDisplayData {
    title: string;
    [key: string]: unknown;
}

export interface PaymentsHomeData {
    paymentsEnabled?: boolean;
    reconciliationEnabled?: boolean;
    initialCardPaymentScreenData?: CardPaymentInitialScreenData;
    initialTerminalPaymentScreenData?: TerminalPaymentInitialScreenData;
    initialEInvoiceScreenData?: EInvoiceInitialScreenData;
}

// Payment-specific interfaces
export interface CardData {
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardholderName: string;
    billingAddress?: Address;
    cardType: CardType | undefined;
}

export interface SidebarBrowserTokenizedCardPaymentWorkflowExecution {
    readonly workflowId: string;
    readonly initialData: JsonValue;
}

export interface ACHData {
    routingNumber: string;
    accountNumber: string;
    accountType: 'checking' | 'savings';
    accountHolderName: string;
    bankName?: string;
}

/**
 * Data structure for RECENT_PAYMENTS state
 * Contains IPW customer identification needed to fetch transactions from vault
 */
export interface RecentPaymentsData {
    ipw_system_id: number; // IpwSystemId pseudo-enum (e.g., 1 = DEALERTRACK)
    ipw_customer_id: string; // External customer ID from IPW system
}

/**
 * Data structure for RECENT_PAYMENT_REQUESTS state
 */
export interface RecentPaymentRequestsData {}

export interface PaymentRequestDetailsData {
    payment_request_uuid: string;
}

/**
 * Data structure for PAYMENTS_AUDIT_LOG state
 * Contains IPW system identification for scoping audit log results
 */
export interface PaymentsAuditLogData {
    ipw_system_id: number; // IpwSystemId pseudo-enum (e.g., 1 = DEALERTRACK)
}

/**
 * Data structure for TRANSACTION_DETAILS state
 * Contains transaction ID and IPW context for navigation
 */
export interface TransactionDetailsData {
    payment_transaction_uuid: string;
    ipw_system_id: number; // Needed for navigation back to Recent Payments
    ipw_customer_id: string; // Needed for navigation back to Recent Payments
    source_state?:
        | typeof StateType.RECENT_PAYMENTS
        | typeof StateType.PAYMENTS_AUDIT_LOG
        | typeof StateType.PAYMENT_REQUEST_DETAILS;
    source_payment_request_uuid?: string;
}

export const CardPaymentScreenMode = {
    INITIAL: 'initial',
    RETRY_MANUAL_ENTRY: 'retry-manual-entry',
    RETRY_BROWSER_TOKENIZED_ENTRY: 'retry-browser-tokenized-entry',
    RETRY_SAVED_CARD: 'retry-saved-card',
} as const;

export const TerminalPaymentScreenMode = {
    INITIAL: 'initial',
    RETRY: 'retry',
} as const;

export const AchPaymentScreenMode = {
    RETRY: 'retry',
} as const;

export const CardPaymentEntryMethod = {
    MANUAL_ENTRY: 'manual-entry',
    BROWSER_TOKENIZED_CARD: 'browser-tokenized-card',
    HOSTED_CARD_TOKENIZATION: 'hosted-card-tokenization',
    SAVED_CARD: 'saved-card',
} as const;

export type CardPaymentEntryMethod =
    (typeof CardPaymentEntryMethod)[keyof typeof CardPaymentEntryMethod];

export interface CardPaymentInitialScreenData {
    transactionDetails: CardPaymentInitialTransactionDetails;
    screenMode: typeof CardPaymentScreenMode.INITIAL;
    disableDetection?: boolean;
}

export interface CardPaymentRetryManualEntryScreenData {
    ipg_system_id: SupportedIpgSystemId;
    transactionDetails: TransactionDetails;
    screenMode: typeof CardPaymentScreenMode.RETRY_MANUAL_ENTRY;
    disableDetection?: boolean;
    terminalDeviceUuid: string;
    cardData: CardData;
    stored_for_future_use: boolean;
}

export interface CardPaymentRetrySavedCardScreenData {
    ipg_system_id: SupportedIpgSystemId;
    transactionDetails: TransactionDetails;
    screenMode: typeof CardPaymentScreenMode.RETRY_SAVED_CARD;
    disableDetection?: boolean;
    terminalDeviceUuid: string;
    paymentMethodTokenUuid: string;
    billingAddress?: Address;
}

export interface CardPaymentRetryBrowserTokenizedEntryScreenData {
    ipg_system_id: SupportedIpgSystemId;
    transactionDetails: TransactionDetails;
    screenMode: typeof CardPaymentScreenMode.RETRY_BROWSER_TOKENIZED_ENTRY;
    disableDetection?: boolean;
    terminalDeviceUuid: string;
    stored_for_future_use: boolean;
    billingAddress?: Address;
}

export type CardPaymentRetryScreenData =
    | CardPaymentRetryManualEntryScreenData
    | CardPaymentRetryBrowserTokenizedEntryScreenData
    | CardPaymentRetrySavedCardScreenData;

export type CardPaymentScreenData = CardPaymentInitialScreenData | CardPaymentRetryScreenData;

export interface AchPaymentRetryScreenData {
    ipg_system_id: SupportedIpgSystemId;
    screenMode: typeof AchPaymentScreenMode.RETRY;
    transactionDetails: TransactionDetails;
    achData: ACHData;
}

export type AchPaymentScreenData = AchPaymentRetryScreenData;

export interface TerminalPaymentInitialScreenData {
    transactionDetails: CardPaymentInitialTransactionDetails;
    screenMode: typeof TerminalPaymentScreenMode.INITIAL;
    disableDetection?: boolean;
}

export interface TerminalPaymentRetryScreenData {
    ipg_system_id: SupportedIpgSystemId;
    transactionDetails: TransactionDetails;
    screenMode: typeof TerminalPaymentScreenMode.RETRY;
    disableDetection?: boolean;
    terminalDeviceUuid: string;
    terminalCardEntryMode: TerminalCardEntryMode;
}

export type TerminalPaymentScreenData =
    | TerminalPaymentInitialScreenData
    | TerminalPaymentRetryScreenData;

export type EInvoiceScreenData = EInvoiceInitialScreenData;

export interface TransactionStatusReceiptData {
    can_get_receipt?: boolean;
    missing_receipt_fields?: string[];
    receipt_generation_inflight?: boolean;
    receipt_file_path?: string;
}

export interface TransactionStatusSharedData extends TransactionStatusReceiptData {
    ipg_system_id: SupportedIpgSystemId;
    transactionDetails: TransactionDetails;
}

export interface TransactionStatusCardData {
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardholderName: string;
    billingAddress?: Address;
}

export interface TransactionStatusAchData {
    accountNumber: string;
    routingNumber: string;
    payeeName: string;
    accountType: 'C' | 'S';
    entryClass: 'Business' | 'Personal';
}

export interface EInvoiceTransactionStatusData extends TransactionStatusSharedData {
    paymentType: typeof StateType.E_INVOICE;
    paymentRequestSendResult: FinalizeSendPaymentRequestResponse;
    recipientEmail?: string;
    recipientPhone?: string;
    sendEmail?: boolean;
    sendSms?: boolean;
}

export interface TerminalPaymentTransactionStatusData extends TransactionStatusSharedData {
    paymentType: typeof StateType.TERMINAL_PAYMENT;
    accountId: string;
    terminalDeviceUuid: string;
    terminalCardEntryMode: TerminalCardEntryMode;
    retryScreenData: TerminalPaymentRetryScreenData;
}

export interface AchPaymentTransactionStatusData extends TransactionStatusSharedData {
    paymentType: typeof StateType.ACH_PAYMENT;
    achData: TransactionStatusAchData;
    retryScreenData: AchPaymentRetryScreenData;
}

export interface CardPaymentTransactionStatusBaseData extends TransactionStatusSharedData {
    paymentType: typeof StateType.CARD_PAYMENT;
    paymentEntryMethod: CardPaymentEntryMethod;
    accountId: string;
    terminalDeviceUuid: string;
    credentialUuid: string;
    organizationUuid: string;
    card_type: CardType;
    pan_prefix?: string;
    surchargeAmount: LosslessNumber;
    convenienceFeeAmount: LosslessNumber;
    totalFeeAmount: LosslessNumber;
    totalAmountPaid: LosslessNumber;
    billingAddress?: Address;
}

export interface CardPaymentManualEntryTransactionStatusData extends CardPaymentTransactionStatusBaseData {
    paymentEntryMethod: typeof CardPaymentEntryMethod.MANUAL_ENTRY;
    cardData: TransactionStatusCardData;
    delegateToken: string;
    delegateDomain: string;
    delegateTerminalReference: string;
    stored_for_future_use: boolean;
    retryScreenData: CardPaymentRetryManualEntryScreenData;
}

export interface CardPaymentSavedMethodTransactionStatusData extends CardPaymentTransactionStatusBaseData {
    paymentEntryMethod: typeof CardPaymentEntryMethod.SAVED_CARD;
    paymentMethodTokenUuid: string;
    retryScreenData: CardPaymentRetrySavedCardScreenData;
}

export interface CardPaymentBrowserTokenizedTransactionStatusData extends CardPaymentTransactionStatusBaseData {
    paymentEntryMethod: typeof CardPaymentEntryMethod.BROWSER_TOKENIZED_CARD;
    workflowExecution: SidebarBrowserTokenizedCardPaymentWorkflowExecution;
    stored_for_future_use: boolean;
    retryScreenData: CardPaymentRetryBrowserTokenizedEntryScreenData;
}

export interface CardPaymentTokenizationTransactionStatusData {
    ipg_system_id: SupportedIpgSystemId;
    paymentType: typeof StateType.CARD_PAYMENT;
    paymentEntryMethod: typeof CardPaymentEntryMethod.HOSTED_CARD_TOKENIZATION;
    completedAtMs: number;
}

/**
 * Data structure for TRANSACTION_STATUS state.
 * Each payment-type branch carries only the fields that are valid for
 * that specific workflow.
 */
export type TransactionStatusData =
    | EInvoiceTransactionStatusData
    | TerminalPaymentTransactionStatusData
    | AchPaymentTransactionStatusData
    | CardPaymentManualEntryTransactionStatusData
    | CardPaymentBrowserTokenizedTransactionStatusData
    | CardPaymentTokenizationTransactionStatusData
    | CardPaymentSavedMethodTransactionStatusData;

/**
 * Base AppState interface with common fields
 */
interface BaseAppState {
    title: string;
    originalListState?: AppState;
    defaultAction?: ListDisplayAction;
    actions?: Array<ListDisplayAction>;
    paymentsEnabled?: boolean;
    reconciliationEnabled?: boolean;
    paymentMethods?: PlatformPaymentMethods;
    /**
     * IPW System ID - should be provided by workflows when initializing the sidebar
     * This identifies which Integrated Payments Website system the sidebar is operating in.
     * Required for payment operations.
     */
    ipw_system_id?: number; // IpwSystemId pseudo-enum (e.g., 1 = DEALERTRACK, 2 = PCPARTPICKER)
}

type ProviderOptionalPaymentAttemptAppState = BaseAppState & {
    ipg_system_id?: SupportedIpgSystemId;
};

type ProviderResolvedPaymentAttemptAppState = BaseAppState & {
    ipg_system_id: SupportedIpgSystemId;
};

type EInvoiceAppState = ProviderOptionalPaymentAttemptAppState & EInvoiceAppStateFields;

export type CardPaymentAppState =
    | (ProviderOptionalPaymentAttemptAppState & {
          type: typeof StateType.CARD_PAYMENT;
          data: null | CardPaymentInitialScreenData;
      })
    | (ProviderResolvedPaymentAttemptAppState & {
          type: typeof StateType.CARD_PAYMENT;
          data: CardPaymentRetryScreenData;
      });

export type TerminalPaymentAppState =
    | (ProviderOptionalPaymentAttemptAppState & {
          type: typeof StateType.TERMINAL_PAYMENT;
          data: null | TerminalPaymentInitialScreenData;
      })
    | (ProviderResolvedPaymentAttemptAppState & {
          type: typeof StateType.TERMINAL_PAYMENT;
          data: TerminalPaymentRetryScreenData;
      });

/**
 * Discriminated union type for AppState
 * Each state type has its own specific data type
 */
export type AppState =
    | (BaseAppState & {
          type: typeof StateType.INIT;
          data: null;
      })
    | (BaseAppState & { type: typeof StateType.LIST_DISPLAY; data: ListDisplayData })
    | (BaseAppState & {
          type: typeof StateType.OBJECT_DISPLAY;
          data: ObjectDisplayData;
      })
    | (BaseAppState & { type: typeof StateType.PAYMENTS_HOME; data: PaymentsHomeData })
    | CardPaymentAppState
    | (ProviderResolvedPaymentAttemptAppState & {
          type: typeof StateType.ACH_PAYMENT;
          data: null | AchPaymentScreenData;
      })
    | TerminalPaymentAppState
    | EInvoiceAppState
    | (ProviderResolvedPaymentAttemptAppState & {
          type: typeof StateType.TRANSACTION_STATUS;
          data: TransactionStatusData;
      })
    | (BaseAppState & { type: typeof StateType.RECENT_PAYMENTS; data: RecentPaymentsData })
    | (BaseAppState & {
          type: typeof StateType.RECENT_PAYMENT_REQUESTS;
          data: RecentPaymentRequestsData;
      })
    | (BaseAppState & {
          type: typeof StateType.PAYMENT_REQUEST_DETAILS;
          data: PaymentRequestDetailsData;
      })
    | (BaseAppState & { type: typeof StateType.PAYMENTS_AUDIT_LOG; data: PaymentsAuditLogData })
    | (BaseAppState & {
          type: typeof StateType.TRANSACTION_DETAILS;
          data: TransactionDetailsData;
      })
    | (BaseAppState & {
          type: typeof StateType.RECONCILE_PAYMENTS;
          data: ReconcilePaymentsData;
      });

export type PaymentsHomeDataPatch = Partial<PaymentsHomeData>;

export type PaymentsHomeStatePatch = {
    readonly type: typeof StateType.PAYMENTS_HOME;
    readonly title?: string;
    readonly data?: PaymentsHomeDataPatch;
    readonly paymentsEnabled?: boolean;
    readonly reconciliationEnabled?: boolean;
    readonly paymentMethods?: PlatformPaymentMethods;
    readonly ipw_system_id?: number;
};

export type AppStatePatch = PaymentsHomeStatePatch;

// Zod Schemas for runtime validation
const ListDisplayActionSchema: z.ZodType<ListDisplayAction> = z.object({
    workflowId: z.string(),
    label: z.string(),
});

const ListDisplayDataSchema: z.ZodType<ListDisplayData> = z.object({
    items: z.array(z.record(z.string(), z.unknown())),
});

const ObjectDisplayDataSchema: z.ZodType<ObjectDisplayData> = z
    .object({
        title: z.string(),
    })
    .passthrough();

export const PaymentsHomeDataSchema: z.ZodType<PaymentsHomeData> = z
    .object({
        initialCardPaymentScreenData: z.lazy(() => CardPaymentInitialScreenDataSchema).optional(),
        initialTerminalPaymentScreenData: z
            .lazy(() => TerminalPaymentInitialScreenDataSchema)
            .optional(),
        initialEInvoiceScreenData: z.lazy(() => EInvoiceInitialScreenDataSchema).optional(),
    })
    .passthrough();

export const PaymentsHomeDataPatchSchema: z.ZodType<PaymentsHomeDataPatch> = z
    .object({
        paymentsEnabled: z.boolean().optional(),
        reconciliationEnabled: z.boolean().optional(),
        initialCardPaymentScreenData: z.lazy(() => CardPaymentInitialScreenDataSchema).optional(),
        initialTerminalPaymentScreenData: z
            .lazy(() => TerminalPaymentInitialScreenDataSchema)
            .optional(),
        initialEInvoiceScreenData: z.lazy(() => EInvoiceInitialScreenDataSchema).optional(),
    })
    .passthrough();

export const PaymentsHomeStatePatchSchema: z.ZodType<PaymentsHomeStatePatch> = z.object({
    type: z.literal(StateType.PAYMENTS_HOME),
    title: z.string().optional(),
    data: PaymentsHomeDataPatchSchema.optional(),
    paymentsEnabled: z.boolean().optional(),
    reconciliationEnabled: z.boolean().optional(),
    paymentMethods: PlatformPaymentMethodsSchema.optional(),
    ipw_system_id: z.number().optional(),
});

export const AppStatePatchSchema: z.ZodType<AppStatePatch> = PaymentsHomeStatePatchSchema;

const RecentPaymentsDataSchema: z.ZodType<RecentPaymentsData> = z.object({
    ipw_system_id: z.number(),
    ipw_customer_id: z.string(),
});

const RecentPaymentRequestsDataSchema: z.ZodType<RecentPaymentRequestsData> = z
    .object({})
    .passthrough();

export const PaymentRequestDetailsDataSchema: z.ZodType<PaymentRequestDetailsData> = z.object({
    payment_request_uuid: z.string().uuid(),
});

const PaymentsAuditLogDataSchema: z.ZodType<PaymentsAuditLogData> = z.object({
    ipw_system_id: z.number(),
});

const TransactionDetailsDataSchema: z.ZodType<TransactionDetailsData> = z.object({
    payment_transaction_uuid: z.string().uuid(),
    ipw_system_id: z.number(),
    ipw_customer_id: z.string(),
    source_state: z
        .enum([
            StateType.RECENT_PAYMENTS,
            StateType.PAYMENTS_AUDIT_LOG,
            StateType.PAYMENT_REQUEST_DETAILS,
        ])
        .optional(),
    source_payment_request_uuid: z.string().uuid().optional(),
});

export const CardDataSchema: z.ZodType<CardData> = z.object({
    cardNumber: z.string(),
    expiryDate: z.string(),
    cvv: z.string(),
    cardholderName: z.string(),
    billingAddress: AddressSchema.optional(),
    cardType: z.enum(CardType).or(z.undefined()),
});

export const CardPaymentInitialScreenDataSchema: z.ZodType<CardPaymentInitialScreenData> = z.object(
    {
        transactionDetails: CardPaymentInitialTransactionDetailsSchema,
        screenMode: z.literal(CardPaymentScreenMode.INITIAL),
        disableDetection: z.boolean().optional(),
    }
);

export const CardPaymentRetryManualEntryScreenDataSchema: z.ZodType<CardPaymentRetryManualEntryScreenData> =
    z.object({
        ipg_system_id: NumericIpgSystemIdSchema,
        transactionDetails: TransactionDetailsSchema,
        screenMode: z.literal(CardPaymentScreenMode.RETRY_MANUAL_ENTRY),
        disableDetection: z.boolean().optional(),
        terminalDeviceUuid: z.string().uuid(),
        cardData: CardDataSchema,
        stored_for_future_use: z.boolean(),
    });

export const CardPaymentRetrySavedCardScreenDataSchema: z.ZodType<CardPaymentRetrySavedCardScreenData> =
    z.object({
        ipg_system_id: NumericIpgSystemIdSchema,
        transactionDetails: TransactionDetailsSchema,
        screenMode: z.literal(CardPaymentScreenMode.RETRY_SAVED_CARD),
        disableDetection: z.boolean().optional(),
        terminalDeviceUuid: z.string().uuid(),
        paymentMethodTokenUuid: z.string().uuid(),
        billingAddress: AddressSchema.optional(),
    });

export const CardPaymentRetryBrowserTokenizedEntryScreenDataSchema: z.ZodType<CardPaymentRetryBrowserTokenizedEntryScreenData> =
    z.object({
        ipg_system_id: NumericIpgSystemIdSchema,
        transactionDetails: TransactionDetailsSchema,
        screenMode: z.literal(CardPaymentScreenMode.RETRY_BROWSER_TOKENIZED_ENTRY),
        disableDetection: z.boolean().optional(),
        terminalDeviceUuid: z.string().uuid(),
        stored_for_future_use: z.boolean(),
        billingAddress: AddressSchema.optional(),
    });

export const CardPaymentScreenDataSchema: z.ZodType<CardPaymentScreenData> = z.union([
    CardPaymentInitialScreenDataSchema,
    CardPaymentRetryManualEntryScreenDataSchema,
    CardPaymentRetryBrowserTokenizedEntryScreenDataSchema,
    CardPaymentRetrySavedCardScreenDataSchema,
]);

export const ACHDataSchema: z.ZodType<ACHData> = z.object({
    routingNumber: z.string(),
    accountNumber: z.string(),
    accountType: z.enum(['checking', 'savings']),
    accountHolderName: z.string(),
    bankName: z.string().optional(),
});

export const AchPaymentRetryScreenDataSchema: z.ZodType<AchPaymentRetryScreenData> = z.object({
    ipg_system_id: NumericIpgSystemIdSchema,
    screenMode: z.literal(AchPaymentScreenMode.RETRY),
    transactionDetails: TransactionDetailsSchema,
    achData: ACHDataSchema,
});

export const AchPaymentScreenDataSchema: z.ZodType<AchPaymentScreenData> =
    AchPaymentRetryScreenDataSchema;

export const TerminalPaymentInitialScreenDataSchema: z.ZodType<TerminalPaymentInitialScreenData> =
    z.object({
        transactionDetails: CardPaymentInitialTransactionDetailsSchema,
        screenMode: z.literal(TerminalPaymentScreenMode.INITIAL),
        disableDetection: z.boolean().optional(),
    });

export const TerminalPaymentRetryScreenDataSchema: z.ZodType<TerminalPaymentRetryScreenData> =
    z.object({
        ipg_system_id: NumericIpgSystemIdSchema,
        transactionDetails: TransactionDetailsSchema,
        screenMode: z.literal(TerminalPaymentScreenMode.RETRY),
        disableDetection: z.boolean().optional(),
        terminalDeviceUuid: z.string().uuid(),
        terminalCardEntryMode: z.enum(TerminalCardEntryMode),
    });

export const TerminalPaymentScreenDataSchema: z.ZodType<TerminalPaymentScreenData> = z.union([
    TerminalPaymentInitialScreenDataSchema,
    TerminalPaymentRetryScreenDataSchema,
]);

export const EInvoiceScreenDataSchema: z.ZodType<EInvoiceScreenData> =
    EInvoiceInitialScreenDataSchema;

export const TransactionStatusReceiptDataSchema: z.ZodType<TransactionStatusReceiptData> = z.object(
    {
        can_get_receipt: z.boolean().optional(),
        missing_receipt_fields: z.array(z.string()).optional(),
        receipt_generation_inflight: z.boolean().optional(),
        receipt_file_path: z.string().optional(),
    }
);

export const TransactionStatusSharedDataSchema: z.ZodType<TransactionStatusSharedData> =
    z.intersection(
        z.object({
            ipg_system_id: NumericIpgSystemIdSchema,
            transactionDetails: TransactionDetailsSchema,
        }),
        TransactionStatusReceiptDataSchema
    );

export const TransactionStatusCardDataSchema: z.ZodType<TransactionStatusCardData> = z.object({
    cardNumber: z.string(),
    expiryDate: z.string(),
    cvv: z.string(),
    cardholderName: z.string(),
    billingAddress: AddressSchema.optional(),
});

export const TransactionStatusAchDataSchema: z.ZodType<TransactionStatusAchData> = z.object({
    accountNumber: z.string(),
    routingNumber: z.string(),
    payeeName: z.string(),
    accountType: z.enum(['C', 'S']),
    entryClass: z.enum(['Business', 'Personal']),
});

export const EInvoiceTransactionStatusDataSchema: z.ZodType<EInvoiceTransactionStatusData> =
    z.intersection(
        TransactionStatusSharedDataSchema,
        z.object({
            paymentType: z.literal(StateType.E_INVOICE),
            paymentRequestSendResult: FinalizeSendPaymentRequestResponseSchema,
            recipientEmail: z.string().email().optional(),
            recipientPhone: z.string().optional(),
            sendEmail: z.boolean().optional(),
            sendSms: z.boolean().optional(),
        })
    );

export const TerminalPaymentTransactionStatusDataSchema: z.ZodType<TerminalPaymentTransactionStatusData> =
    z.intersection(
        TransactionStatusSharedDataSchema,
        z.object({
            paymentType: z.literal(StateType.TERMINAL_PAYMENT),
            accountId: z.string().min(1),
            terminalDeviceUuid: z.string().uuid(),
            terminalCardEntryMode: z.enum(TerminalCardEntryMode),
            retryScreenData: TerminalPaymentRetryScreenDataSchema,
        })
    );

export const AchPaymentTransactionStatusDataSchema: z.ZodType<AchPaymentTransactionStatusData> =
    z.intersection(
        TransactionStatusSharedDataSchema,
        z.object({
            paymentType: z.literal(StateType.ACH_PAYMENT),
            achData: TransactionStatusAchDataSchema,
            retryScreenData: AchPaymentRetryScreenDataSchema,
        })
    );

export const CardPaymentTransactionStatusBaseDataSchema: z.ZodType<CardPaymentTransactionStatusBaseData> =
    z.intersection(
        TransactionStatusSharedDataSchema,
        z.object({
            paymentType: z.literal(StateType.CARD_PAYMENT),
            paymentEntryMethod: z.enum([
                CardPaymentEntryMethod.MANUAL_ENTRY,
                CardPaymentEntryMethod.BROWSER_TOKENIZED_CARD,
                CardPaymentEntryMethod.SAVED_CARD,
            ]),
            accountId: z.string().min(1),
            terminalDeviceUuid: z.string().uuid(),
            credentialUuid: z.string().uuid(),
            organizationUuid: z.string().uuid(),
            card_type: z.enum(CardType),
            pan_prefix: z
                .string()
                .regex(/^\d{6,9}$/)
                .optional(),
            surchargeAmount: JsonNonnegativeLosslessNumberInputSchema,
            convenienceFeeAmount: JsonNonnegativeLosslessNumberInputSchema,
            totalFeeAmount: JsonNonnegativeLosslessNumberInputSchema,
            totalAmountPaid: JsonNonnegativeLosslessNumberInputSchema,
            billingAddress: AddressSchema.optional(),
        })
    );

export const CardPaymentManualEntryTransactionStatusDataSchema: z.ZodType<CardPaymentManualEntryTransactionStatusData> =
    z.intersection(
        CardPaymentTransactionStatusBaseDataSchema,
        z.object({
            paymentEntryMethod: z.literal(CardPaymentEntryMethod.MANUAL_ENTRY),
            cardData: TransactionStatusCardDataSchema,
            delegateToken: z.string().min(1),
            delegateDomain: z.string().min(1),
            delegateTerminalReference: z.string().min(1),
            stored_for_future_use: z.boolean(),
            retryScreenData: CardPaymentRetryManualEntryScreenDataSchema,
        })
    );

export const CardPaymentSavedMethodTransactionStatusDataSchema: z.ZodType<CardPaymentSavedMethodTransactionStatusData> =
    z.intersection(
        CardPaymentTransactionStatusBaseDataSchema,
        z.object({
            paymentEntryMethod: z.literal(CardPaymentEntryMethod.SAVED_CARD),
            paymentMethodTokenUuid: z.string().uuid(),
            retryScreenData: CardPaymentRetrySavedCardScreenDataSchema,
        })
    );

export const CardPaymentBrowserTokenizedTransactionStatusDataSchema: z.ZodType<CardPaymentBrowserTokenizedTransactionStatusData> =
    z.intersection(
        CardPaymentTransactionStatusBaseDataSchema,
        z.object({
            paymentEntryMethod: z.literal(CardPaymentEntryMethod.BROWSER_TOKENIZED_CARD),
            workflowExecution: z
                .object({
                    workflowId: z.string().min(1),
                    initialData: JsonValueSchema,
                })
                .strict(),
            stored_for_future_use: z.boolean(),
            retryScreenData: CardPaymentRetryBrowserTokenizedEntryScreenDataSchema,
        })
    );

export const CardPaymentTokenizationTransactionStatusDataSchema: z.ZodType<CardPaymentTokenizationTransactionStatusData> =
    z
        .object({
            ipg_system_id: NumericIpgSystemIdSchema,
            paymentType: z.literal(StateType.CARD_PAYMENT),
            paymentEntryMethod: z.literal(CardPaymentEntryMethod.HOSTED_CARD_TOKENIZATION),
            completedAtMs: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
        })
        .strict();

export const TransactionStatusDataSchema: z.ZodType<TransactionStatusData> = z.union([
    EInvoiceTransactionStatusDataSchema,
    TerminalPaymentTransactionStatusDataSchema,
    AchPaymentTransactionStatusDataSchema,
    CardPaymentManualEntryTransactionStatusDataSchema,
    CardPaymentBrowserTokenizedTransactionStatusDataSchema,
    CardPaymentTokenizationTransactionStatusDataSchema,
    CardPaymentSavedMethodTransactionStatusDataSchema,
]);

// Base schema fields shared by all AppState variants
const BaseAppStateSchemaFields = {
    title: z.string(),
    originalListState: z.lazy(() => AppStateSchema).optional(),
    defaultAction: ListDisplayActionSchema.optional(),
    actions: z.array(ListDisplayActionSchema).optional(),
    paymentsEnabled: z.boolean().optional(),
    reconciliationEnabled: z.boolean().optional(),
    paymentMethods: PlatformPaymentMethodsSchema.optional(),
    ipw_system_id: z.number().optional(),
};

type CardPaymentAppStateWire = ProviderOptionalPaymentAttemptAppState & {
    type: typeof StateType.CARD_PAYMENT;
    data: null | CardPaymentScreenData;
};

type TerminalPaymentAppStateWire = ProviderOptionalPaymentAttemptAppState & {
    type: typeof StateType.TERMINAL_PAYMENT;
    data: null | TerminalPaymentScreenData;
};

type AppStateWire =
    | Exclude<AppState, { type: typeof StateType.CARD_PAYMENT | typeof StateType.TERMINAL_PAYMENT }>
    | CardPaymentAppStateWire
    | TerminalPaymentAppStateWire;

/**
 * Discriminated union schema for AppState
 */
export const AppStateDiscriminatedUnionSchema: z.ZodType<AppStateWire> = z.discriminatedUnion(
    'type',
    [
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.INIT),
            data: z.null(),
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.LIST_DISPLAY),
            data: ListDisplayDataSchema,
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.OBJECT_DISPLAY),
            data: ObjectDisplayDataSchema,
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.PAYMENTS_HOME),
            data: PaymentsHomeDataSchema,
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.CARD_PAYMENT),
            ipg_system_id: NumericIpgSystemIdSchema.optional(),
            data: z.union([z.null(), CardPaymentScreenDataSchema]),
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.ACH_PAYMENT),
            ipg_system_id: NumericIpgSystemIdSchema,
            data: z.union([z.null(), AchPaymentScreenDataSchema]),
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.TERMINAL_PAYMENT),
            ipg_system_id: NumericIpgSystemIdSchema.optional(),
            data: z.union([z.null(), TerminalPaymentScreenDataSchema]),
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.E_INVOICE),
            ipg_system_id: NumericIpgSystemIdSchema.optional(),
            data: z.union([z.null(), EInvoiceScreenDataSchema]),
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.TRANSACTION_STATUS),
            ipg_system_id: NumericIpgSystemIdSchema,
            data: TransactionStatusDataSchema,
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.RECENT_PAYMENTS),
            data: RecentPaymentsDataSchema,
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.RECENT_PAYMENT_REQUESTS),
            data: RecentPaymentRequestsDataSchema,
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.PAYMENT_REQUEST_DETAILS),
            data: PaymentRequestDetailsDataSchema,
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.PAYMENTS_AUDIT_LOG),
            data: PaymentsAuditLogDataSchema,
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.TRANSACTION_DETAILS),
            data: TransactionDetailsDataSchema,
        }),
        z.object({
            ...BaseAppStateSchemaFields,
            type: z.literal(StateType.RECONCILE_PAYMENTS),
            data: ReconcilePaymentsDataSchema,
        }),
    ]
);

export const AppStateSchema: z.ZodType<AppState> = AppStateDiscriminatedUnionSchema.superRefine(
    (state: AppStateWire, context: z.RefinementCtx): void => {
        const isCardRetryWithoutProvider: boolean =
            state.type === StateType.CARD_PAYMENT &&
            state.data !== null &&
            state.data.screenMode !== CardPaymentScreenMode.INITIAL &&
            state.ipg_system_id === undefined;
        const isTerminalRetryWithoutProvider: boolean =
            state.type === StateType.TERMINAL_PAYMENT &&
            state.data !== null &&
            state.data.screenMode === TerminalPaymentScreenMode.RETRY &&
            state.ipg_system_id === undefined;
        if (isCardRetryWithoutProvider || isTerminalRetryWithoutProvider) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Retry payment state requires an active payment provider.',
                path: ['ipg_system_id'],
            });
        }
        if (
            'ipg_system_id' in state &&
            state.ipg_system_id !== undefined &&
            state.data !== null &&
            'ipg_system_id' in state.data &&
            state.ipg_system_id !== state.data.ipg_system_id
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Payment state provider must match its screen data provider.',
                path: ['data', 'ipg_system_id'],
            });
        }
        if (
            state.type === StateType.TRANSACTION_STATUS &&
            'retryScreenData' in state.data &&
            state.data.ipg_system_id !== state.data.retryScreenData.ipg_system_id
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Retry provider must match the transaction status provider.',
                path: ['data', 'retryScreenData', 'ipg_system_id'],
            });
        }
    }
).transform((state: AppStateWire): AppState => {
    if (state.type === StateType.CARD_PAYMENT) {
        if (state.data === null || state.data.screenMode === CardPaymentScreenMode.INITIAL) {
            const initialState: CardPaymentAppState = {
                ...state,
                data: state.data,
            };
            return initialState;
        }
        if (state.ipg_system_id === undefined) {
            return z.NEVER;
        }
        const retryState: CardPaymentAppState = {
            ...state,
            ipg_system_id: state.ipg_system_id,
            data: state.data,
        };
        return retryState;
    }
    if (state.type === StateType.TERMINAL_PAYMENT) {
        if (state.data === null || state.data.screenMode === TerminalPaymentScreenMode.INITIAL) {
            const initialState: TerminalPaymentAppState = {
                ...state,
                data: state.data,
            };
            return initialState;
        }
        if (state.ipg_system_id === undefined) {
            return z.NEVER;
        }
        const retryState: TerminalPaymentAppState = {
            ...state,
            ipg_system_id: state.ipg_system_id,
            data: state.data,
        };
        return retryState;
    }
    return state;
});

export type IpsPaymentsHomeAppState = Extract<AppState, { type: typeof StateType.PAYMENTS_HOME }>;

// Physical version of the IPS renderer's independently deployed workspace-state response.
export interface IpsWorkspaceStateV1 {
    readonly version: 1;
    readonly appState: AppState;
    readonly paymentsHomeState?: IpsPaymentsHomeAppState;
}

const IpsPaymentsHomeAppStateSchema: z.ZodType<IpsPaymentsHomeAppState> = AppStateSchema.transform(
    (appState, context) => {
        if (appState.type !== StateType.PAYMENTS_HOME) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Remembered IPS state must be Payments Home.',
            });
            return z.NEVER;
        }
        return appState;
    }
);

const IpsWorkspaceCapturableScreens = {
    [StateType.INIT]: true,
    [StateType.LIST_DISPLAY]: true,
    [StateType.OBJECT_DISPLAY]: true,
    [StateType.PAYMENTS_HOME]: true,
    [StateType.CARD_PAYMENT]: false,
    [StateType.ACH_PAYMENT]: false,
    [StateType.TERMINAL_PAYMENT]: false,
    [StateType.E_INVOICE]: false,
    [StateType.TRANSACTION_STATUS]: false,
    [StateType.RECENT_PAYMENTS]: true,
    [StateType.RECENT_PAYMENT_REQUESTS]: true,
    [StateType.PAYMENT_REQUEST_DETAILS]: true,
    [StateType.PAYMENTS_AUDIT_LOG]: true,
    [StateType.TRANSACTION_DETAILS]: true,
    [StateType.RECONCILE_PAYMENTS]: true,
} satisfies Record<StateType, boolean>;

export const IpsWorkspaceStateV1Schema: z.ZodType<IpsWorkspaceStateV1> = z
    .object({
        version: z.literal(1),
        appState: AppStateSchema,
        paymentsHomeState: IpsPaymentsHomeAppStateSchema.optional(),
    })
    .strict()
    .superRefine((workspaceState, context): void => {
        const roots: readonly (readonly [AppState | undefined, string])[] = [
            [workspaceState.appState, 'appState'],
            [workspaceState.paymentsHomeState, 'paymentsHomeState'],
        ];
        for (const [root, rootPath] of roots) {
            let appState: AppState | undefined = root;
            let depth = 0;
            while (appState !== undefined) {
                if (!IpsWorkspaceCapturableScreens[appState.type]) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `IPS screen ${appState.type} cannot be safely captured during payment activity.`,
                        path: [
                            rootPath,
                            ...Array.from({ length: depth }, () => 'originalListState'),
                            'type',
                        ],
                    });
                    break;
                }
                appState = appState.originalListState;
                depth += 1;
            }
        }
    });
